<template>
    <div class="flex min-h-screen bg-gray-50">
        <!-- Sidebar -->
        <aside class="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
            <!-- Logo -->
            <div class="flex items-center px-6 py-5 border-b border-gray-200">
                <img
                    :src="logoSrc"
                    alt="Logo"
                    class="h-16 w-full object-contain object-left"
                    @error="onLogoError"
                />
            </div>

            <!-- Navigation -->
            <nav class="flex-1 px-3 py-4 space-y-1">
                <router-link
                    v-for="item in navItems"
                    :key="item.to"
                    :to="item.to"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-normal font-medium transition-colors duration-150"
                    :class="isActive(item.to)
                        ? 'bg-primary-500 bg-opacity-30 text-primary-800'
                        : 'text-gray-600 hover:bg-gray-100'"
                >
                    <component :is="item.icon" :size="32" class="flex-shrink-0" />
                    {{ item.label }}
                </router-link>
            </nav>

            <!-- User info + logout -->
            <div class="px-4 py-4 border-t border-gray-200">
                <div v-if="user" class="mb-3">
                    <p class="text-sm font-semibold text-gray-800 truncate">{{ user.firstName }} {{ user.lastName }}</p>
                    <p class="text-xs text-gray-400 truncate">{{ user.email }}</p>
                </div>
                <button
                    class="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    @click="logout"
                >
                    <LogOut class="w-4 h-4" />
                    {{ $t('nav.signOut') }} 
                </button>
            </div>
        </aside>

        <!-- Main content -->
        <main class="flex-1 overflow-y-auto overflow-x-auto">
            <div class="max-w-2xl min-w-[28rem] px-8 py-10">
                <div v-if="booting" class="flex items-center justify-center py-24">
                    <div class="w-8 h-8 rounded-full border-2 border-gray-200 border-t-primary-500 animate-spin"></div>
                </div>
                <router-view v-else></router-view>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { User, ShieldCheck, Bell, LogOut } from 'lucide-vue-next';
import { useStore } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import Authentication from '@/plugins/authentication';
import { useI18n } from 'vue-i18n';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const booting = ref(!store.state.user || !store.state.languages.length);
const user = computed(() => store.state.user);

const logoSrc = `${process.env.BASE_URL}solutions/${process.env.VUE_APP_SOLUTION_PATH}/assets/images/sidebar-logo.svg`;

const navItems = computed(() => [
    { to: '/account/profile', label: t('nav.profile'), icon: User },
    { to: '/account/security', label: t('nav.security'), icon: ShieldCheck },
    // { to: '/account/notifications', label: t('nav.notifications'), icon: Bell }, // TODO: enable when notifications API is supported
]);

function isActive(path: string) {
    return route.path.startsWith(path);
}

function onLogoError(e: Event) {
    (e.target as HTMLImageElement).style.display = 'none';
}

async function logout() {
    await Authentication.logout();
}

onMounted(async () => {
    try {
        await Promise.all([
            store.state.user ? Promise.resolve() : store.dispatch(ActionType.FetchProfile),
            store.state.languages.length ? Promise.resolve() : store.dispatch(ActionType.FetchLanguages),
        ]);
    } catch {
        // Non-fatal — page still renders
    } finally {
        booting.value = false;
    }
});
</script>
